import React, { useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import type { SvgIconComponent } from "@mui/icons-material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { Button } from "components/common/Button";
import { Callout } from "components/common/Callout";
import { SectionRule } from "components/common/SectionRule";
import { Inline } from "components/common/Stack";
import { StatusChip } from "components/common/StatusChip";
import { TextInput } from "components/common/TextInput";
import { TooltipButton } from "components/common/TooltipButton";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { Dialog } from "components/main/utils/Dialog";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { spendLabel, usdMicros } from "@/components/main/apps/app-format";
import { InlineFigure } from "@/components/main/apps/limit-field";
import { RefusalCallout } from "@/components/authentication/consent/RefusalCallout";
import "@/components/main/utils/tokens/AccessConnectPanel.css";

interface ProviderKeyRow {
  id: string;
  name: string;
  key: string;
  /** The key's last characters, never the key. */
  hint: string | null;
  key_hint_text: string;
  /** The meta line: when it was set, what it serves. */
  detail?: string;
  /** Monthly limit in cents; `null` is unlimited. */
  limit_cents?: number | null;
  /** Micro-dollars spent on the key this period; absent where nobody counts
   *  it. */
  spent_micros?: number;
}

type KeyMutation<V> = UseMutationResult<unknown, Error, V>;

/// The AI clients page's squares: the same brands, the same brand colours.
const PROVIDER_MARKS: Record<string, string> = {
  anthropic: "var(--color-brand-claude)",
  openai: "var(--color-brand-chatgpt)",
  google: "var(--client-mark-gemini)",
};

const ProviderRow: React.FC<{
  provider: ProviderKeyRow;
  Icon: SvgIconComponent | null;
  onSet: () => void;
  onRemove: () => void;
  onLimit?: (cents: number | null) => Promise<unknown>;
}> = ({ provider, Icon, onSet, onRemove, onLimit }) => {
  const onFile = provider.key === "on_file";
  const setLabel = onFile ? "Replace key" : "Set key";
  const spend =
    onFile && provider.spent_micros != null
      ? spendLabel(provider.spent_micros, provider.limit_cents)
      : null;
  return (
    <div className="rounded-app group/card">
      <div
        className="group flex items-center gap-2 px-1 py-1.5 rounded-app select-none cursor-pointer group-hover/card:bg-bg-hover transition-colors"
        tabIndex={0}
        onClick={onSet}
      >
        <span className="flex items-center justify-center size-6 shrink-0 text-fg-muted">
          {PROVIDER_MARKS[provider.id] ? (
            <span
              aria-hidden
              className="client-mark size-3.5"
              style={
                {
                  "--client-mark-color": PROVIDER_MARKS[provider.id],
                } as React.CSSProperties
              }
            />
          ) : (
            Icon && <Icon sx={{ fontSize: 20 }} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <Inline gap="xs" className="min-w-0 items-center">
            <span className="truncate text-fg-medium">{provider.name}</span>
            {onFile ? (
              <StatusChip tone="muted">key {provider.hint}</StatusChip>
            ) : (
              <StatusChip tone="warning">no key</StatusChip>
            )}
            {spend && (
              <span
                className={`inline-flex items-baseline gap-1.5 font-mono text-fg-muted-regular-small ${spend.near ? "text-warning" : ""}`}
                // The row opens the key dialog; the field is its own.
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {usdMicros(provider.spent_micros ?? 0)}
                {onLimit && (
                  <InlineFigure
                    value={provider.limit_cents ?? null}
                    onSave={onLimit}
                    label={`${provider.name} key's monthly limit`}
                    prefix="of"
                    hint="Set monthly limit"
                    clearable
                  />
                )}
              </span>
            )}
          </Inline>
          {provider.detail && (
            <span className="text-fg-muted-regular-small font-mono truncate block leading-tight">
              {provider.detail}
            </span>
          )}
        </div>
        <div
          className="flex items-center gap-1 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <TooltipButton
            title={setLabel}
            variant="contextual"
            size="xs"
            onClick={onSet}
            aria-label={`${setLabel} for ${provider.name}`}
          >
            <KeyOutlinedIcon sx={{ fontSize: 14 }} />
          </TooltipButton>
          <TooltipButton
            title={onFile ? "Remove key" : "No key to remove"}
            variant="contextual"
            size="xs"
            disabled={!onFile}
            onClick={onRemove}
            aria-label={`Remove the ${provider.name} key`}
          >
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
          </TooltipButton>
        </div>
      </div>
    </div>
  );
};

export const ProviderKeysPanel: React.FC<{
  label: string;
  /// `null` leaves the section rule bare, as a settings page's other rules are.
  Icon?: SvgIconComponent | null;
  intro?: string;
  providers: ProviderKeyRow[];
  put: KeyMutation<{ provider: string; apiKey: string }>;
  remove: KeyMutation<string>;
  /** Present where a key's monthly limit may be set. */
  limit?: KeyMutation<{ provider: string; limitCents: number | null }>;
  children?: React.ReactNode;
}> = ({
  label,
  Icon = AutoAwesomeOutlinedIcon,
  intro,
  providers,
  put,
  remove,
  limit,
  children,
}) => {
  const [editing, setEditing] = useState<ProviderKeyRow | null>(null);
  const [removing, setRemoving] = useState<ProviderKeyRow | null>(null);
  const [apiKey, setApiKey] = useState("");

  const save = () => {
    if (!editing || !apiKey.trim()) return;
    put.mutate(
      { provider: editing.id, apiKey: apiKey.trim() },
      {
        onSuccess: () => {
          showNotificationGlobal(`${editing.name} key saved.`, "success");
          setEditing(null);
          setApiKey("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="sticky top-0 z-10 bg-bg overflow-hidden">
        <SectionRule
          inset={!!Icon}
          icon={Icon && <Icon sx={{ fontSize: 14 }} />}
          label={label}
        />
      </div>
      {intro && (
        <p className="text-fg-muted-regular-small px-1" style={{ margin: 0 }}>
          {intro}
        </p>
      )}
      <div className="flex flex-col">
        {providers.map((p) => (
          <ProviderRow
            key={p.id}
            provider={p}
            Icon={Icon}
            onSet={() => {
              put.reset();
              setApiKey("");
              setEditing(p);
            }}
            onRemove={() => {
              remove.reset();
              setRemoving(p);
            }}
            onLimit={
              limit
                ? (limitCents) =>
                    limit.mutateAsync({ provider: p.id, limitCents })
                : undefined
            }
          />
        ))}
        <ConfirmDialog
          open={removing !== null}
          title={removing ? `Remove the ${removing.name} key?` : ""}
          message="Apps running on it pause at their next run until a key is back."
          confirmText="Remove"
          confirmVariant="destructive"
          isLoading={remove.isPending}
          onConfirm={() => {
            if (!removing) return;
            const name = removing.name;
            remove.mutate(removing.id, {
              onSuccess: () => {
                showNotificationGlobal(`${name} key removed.`, "success");
                setRemoving(null);
              },
            });
          }}
          onCancel={() => setRemoving(null)}
        />
        {remove.error && (
          <Callout variant="error">{remove.error.message}</Callout>
        )}
        <Dialog
          open={editing !== null}
          title={editing ? `${editing.name} API key` : ""}
          ariaLabel="Provider key"
          onClose={() => setEditing(null)}
          actions={
            <>
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!apiKey.trim()}
                loading={put.isPending}
                onClick={save}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-1.5">
            <TextInput
              type="password"
              autoComplete="off"
              className="input-custom-sm font-mono"
              value={apiKey}
              onChange={setApiKey}
              onKeyDown={(e) => e.key === "Enter" && save()}
              placeholder={editing?.key_hint_text || "Paste the key"}
              ariaLabel={`${editing?.name} API key`}
              autoFocus
            />
            <span className="text-fg-muted-regular-small">
              {editing?.key === "on_file" && `Replaces key ${editing.hint}. `}
              Checked with {editing?.name} before it's saved.
            </span>
          </div>
          <RefusalCallout error={put.error} />
        </Dialog>
      </div>
      {children}
    </div>
  );
};
